import type {
  DsdEditorMatchedParagraph,
  DsdEditorParagraphSegment,
} from "@/apis/dsd-editor/route";

// 렌더 문단(HTML→textContent)과 백엔드 문단을 잇는 키.
// 파싱 문단 아이템에는 group/para index가 없어 chunkId + 정규화 텍스트로 매칭한다.
const normalizeParagraphText = (text: string) =>
  text.replace(/\s+/g, " ").trim();

export const matchedParagraphKey = (chunkId: string, text: string) =>
  `${chunkId}\n${normalizeParagraphText(text)}`;

/** chunkId + 정규화 텍스트 → 세그먼트. 매칭된 문단만 대치 렌더에 사용 */
export const buildMatchedParagraphSegments = (
  paragraphs: DsdEditorMatchedParagraph[],
): ReadonlyMap<string, DsdEditorParagraphSegment[]> => {
  const map = new Map<string, DsdEditorParagraphSegment[]>();

  for (const paragraph of paragraphs) {
    const text = paragraph.segments.map((segment) => segment.text).join("");
    map.set(matchedParagraphKey(paragraph.chunkId, text), paragraph.segments);
  }

  return map;
};

/** value 세그먼트의 occurrence id 목록 (셀처럼 상태/선택에 편입하기 위함) */
export const getMatchedParagraphValueIds = (
  paragraphs: DsdEditorMatchedParagraph[],
): string[] =>
  paragraphs.flatMap((paragraph) =>
    paragraph.segments
      .filter(
        (
          segment,
        ): segment is DsdEditorParagraphSegment & { occurrenceId: string } =>
          segment.type === "value" && !!segment.occurrenceId,
      )
      .map((segment) => segment.occurrenceId),
  );

/**
 * keep 을 통과한 value 세그먼트만 남기고 나머지는 평문(text)으로 내린다.
 * 문단 값 렌더는 셀과 달리 클릭·색을 스스로 거르지 않는다 — matchedParagraphs 에는 여러 매칭(본문·주석, 주석·주석)의
 * 문단이 섞여 오므로, 지금 모드에서 선택할 수 없는 값이 후보처럼 칠해지고 눌리지 않게 셀과 같은 규칙으로 거른다
 */
export const keepParagraphValueSegments = (
  segmentsByKey: ReadonlyMap<string, DsdEditorParagraphSegment[]>,
  keep: (occurrenceId: string) => boolean,
): ReadonlyMap<string, DsdEditorParagraphSegment[]> => {
  const map = new Map<string, DsdEditorParagraphSegment[]>();
  for (const [key, segments] of segmentsByKey)
    map.set(
      key,
      segments.map((segment) =>
        segment.type === "value" &&
        !(segment.occurrenceId && keep(segment.occurrenceId))
          ? { type: "text", text: segment.text }
          : segment,
      ),
    );
  return map;
};
